/**
 * Build-time configuration.
 *
 * Nothing here is secret — every `VITE_` variable ships to the browser. The
 * list is deliberately short: docs/04 §1.5 is explicit that clients hardcode no
 * environment-specific address, so the vault, the collateral token, the chain
 * id, the EIP-712 domain and the whole `ws` block all arrive from
 * `GET /config` at runtime. What is left is the two origins needed to *reach*
 * that endpoint, plus the client version string.
 */

interface RawEnv {
  VITE_API_BASE_URL?: string
  VITE_WS_URL?: string
  VITE_APP_VERSION?: string
  VITE_UD_CHAIN_PINS?: string
}

const raw: RawEnv = (import.meta.env ?? {}) as RawEnv

/**
 * Same-origin by default. §1's single-`'self'` CSP is built on the document,
 * JS, CSS and font all shipping from one place; the dev proxy in
 * `vite.config.ts` makes `/v1` work locally without a second origin.
 */
export const API_BASE_URL: string = raw.VITE_API_BASE_URL?.replace(/\/+$/, '') ?? '/v1'

/**
 * The socket URL is a **fallback**. `config.ws.url` is authoritative once
 * `GET /config` has answered, and nothing in `config.ws` may be a constant in
 * this client (register §D2).
 */
export const WS_URL_FALLBACK: string = raw.VITE_WS_URL ?? ''

export const APP_VERSION: string = raw.VITE_APP_VERSION ?? '0.0.0-dev'

/**
 * The contracts this deployment will let a user approve, fund and sign against.
 *
 * **This is the one exception to the paragraph at the top of this file, and it
 * is not a contradiction of it.** §1.5's rule is that the *client* hardcodes no
 * environment-specific address, so the exchange can rotate without a release.
 * What that rule left open is that every address the client acts on is then
 * chosen by whoever answers `GET /config` — and `approve`, `deposit` and
 * `verifyingContract` are three ways to lose money to a contract the venue
 * named (`docs/17`, CRITICAL; §F48d). A pin closes it without re-hardcoding
 * anything: the value lives in the deployment's own environment, the source
 * ships with no address in it, and rotating the exchange stays a deploy rather
 * than a release.
 *
 * `{"42161": {"exchange": "0x…", "collateral": "0x…"}}`, keyed by chain id.
 * **Unset is legal and is the development case** — the mock mints its own
 * addresses — and it degrades to trust-on-first-use rather than to trusting
 * everything (`domain/chain-trust.ts`). A pin written before `docs/06` §F93
 * also names `conditional_tokens`; that member is ignored, because `/config`
 * no longer publishes one and there is nothing left to approve on it.
 *
 * Parsed once, and a malformed value is `{}` rather than a throw: a broken pin
 * must not take the market page down for an anonymous visitor who is never
 * going to sign anything. It shows up where it matters, as an unpinned verdict
 * on the screens that move money.
 */
export const CHAIN_PINS: Readonly<Record<string, { exchange: string; collateral: string }>> = parsePins(
  raw.VITE_UD_CHAIN_PINS,
)

function parsePins(value: string | undefined): Record<string, { exchange: string; collateral: string }> {
  if (!value) return {}
  try {
    const parsed: unknown = JSON.parse(value)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const out: Record<string, { exchange: string; collateral: string }> = {}
    for (const [chainId, entry] of Object.entries(parsed as Record<string, unknown>)) {
      const pin = entry as Record<string, unknown> | null
      if (typeof pin?.exchange === 'string' && typeof pin.collateral === 'string') {
        out[chainId] = { exchange: pin.exchange, collateral: pin.collateral }
      }
    }
    return out
  } catch {
    return {}
  }
}

/** `X-UD-Client` on every request — echoed in server logs, aids support (§2.9). */
export const CLIENT_ID = `uptik-web/${APP_VERSION}`
